import { t } from '@lingui/macro';
import { formatDecimal, transEnum } from '@luminovo/commons';
import { FieldNumericControlled, Flexbox, FormItem, Tag, Text, colorSystem } from '@luminovo/design-system';
import { SolutionTag } from '@luminovo/http-client';
import { solutionTagTranslations } from '@luminovo/sourcing-core';
import { Box, InputAdornment, Table, TableBody, TableCell, TableHead, TableRow, styled } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { SourcingScenarioFormValues } from './types';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${colorSystem.neutral[2]}`,
    '&.MuiTableCell-head': {
        backgroundColor: colorSystem.neutral[0],
        height: '32px',
    },
}));

const StyledTable = styled(Table)({
    '& tr:last-child td': {
        borderBottom: 'none',
    },
});

const OfferAgeLimitSection = () => {
    const { control } = useFormContext<SourcingScenarioFormValues>();
    const [oldAfterUpdateDays, outdatedAfterUpdateDays, expiringBeforeValidityDate, expiredAfterValidityDate] =
        useWatch({
            control,
            name: [
                'solutionTagsPreferences.offerAgeLimit.oldAfterUpdateDays',
                'solutionTagsPreferences.offerAgeLimit.outdatedAfterUpdateDays',
                'solutionTagsPreferences.offerAgeLimit.expiringBeforeValidityDate',
                'solutionTagsPreferences.offerAgeLimit.expiredAfterValidityDate',
            ],
        });

    return (
        <FormItem
            label={t`Offer age limit`}
            variant="description-inlined"
            description={t`Configure rules to tag offers based on their age and valid until date. These tags help identify outdated or expiring offers in your sourcing solutions.`}
        >
            <Box
                sx={{
                    width: '800px',
                    border: `1px solid ${colorSystem.neutral[2]}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                }}
            >
                <StyledTable>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>
                                <Text variant="body-small-semibold">{t`Name`}</Text>
                            </StyledTableCell>
                            <StyledTableCell>
                                <Text variant="body-small-semibold">{t`Condition`}</Text>
                            </StyledTableCell>
                            <StyledTableCell>
                                <Text variant="body-small-semibold">{t`Parameter`}</Text>
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <StyledTableCell>
                                <Tag label={transEnum(SolutionTag.Old, solutionTagTranslations)} color="yellow" />
                            </StyledTableCell>
                            <StyledTableCell>
                                <Flexbox gap={'4px'} alignItems="center">
                                    <Text>{t`Last update is older than `}</Text>
                                    <Text variant="body-semibold">
                                        {formatDecimal(oldAfterUpdateDays, { ifAbsent: '∞' })}
                                    </Text>
                                    <Text>{t` days`}</Text>
                                </Flexbox>
                            </StyledTableCell>
                            <StyledTableCell>
                                <FieldNumericControlled
                                    name="solutionTagsPreferences.offerAgeLimit.oldAfterUpdateDays"
                                    control={control}
                                    min={0}
                                    isInteger
                                    FieldProps={{
                                        placeholder: t`Leave empty to disable`,
                                        size: 'small',
                                        InputProps: {
                                            endAdornment: <InputAdornment position="end">{t`days`}</InputAdornment>,
                                        },
                                    }}
                                />
                            </StyledTableCell>
                        </TableRow>

                        <TableRow>
                            <StyledTableCell>
                                <Tag label={transEnum(SolutionTag.Outdated, solutionTagTranslations)} color="red" />
                            </StyledTableCell>
                            <StyledTableCell>
                                <Flexbox gap={'4px'} alignItems="center">
                                    <Text>{t`Last update is older than `}</Text>
                                    <Text variant="body-semibold">
                                        {formatDecimal(outdatedAfterUpdateDays, { ifAbsent: '∞' })}
                                    </Text>
                                    <Text>{t` days`}</Text>
                                </Flexbox>
                            </StyledTableCell>
                            <StyledTableCell>
                                <FieldNumericControlled
                                    name="solutionTagsPreferences.offerAgeLimit.outdatedAfterUpdateDays"
                                    control={control}
                                    min={0}
                                    isInteger
                                    FieldProps={{
                                        placeholder: t`Leave empty to disable`,
                                        size: 'small',
                                        InputProps: {
                                            endAdornment: <InputAdornment position="end">{t`days`}</InputAdornment>,
                                        },
                                    }}
                                />
                            </StyledTableCell>
                        </TableRow>

                        <TableRow>
                            <StyledTableCell>
                                <Tag label={transEnum(SolutionTag.Expiring, solutionTagTranslations)} color="yellow" />
                            </StyledTableCell>
                            <StyledTableCell>
                                <Flexbox gap={'4px'} alignItems="center">
                                    <Text>{t`Valid until date is less than `}</Text>
                                    <Text variant="body-semibold">
                                        {formatDecimal(expiringBeforeValidityDate, { ifAbsent: '∞' })}
                                    </Text>
                                    <Text>{t` days away`}</Text>
                                </Flexbox>
                            </StyledTableCell>
                            <StyledTableCell>
                                <FieldNumericControlled
                                    name="solutionTagsPreferences.offerAgeLimit.expiringBeforeValidityDate"
                                    control={control}
                                    min={0}
                                    isInteger
                                    FieldProps={{
                                        placeholder: t`Leave empty to disable`,
                                        size: 'small',
                                        InputProps: {
                                            endAdornment: <InputAdornment position="end">{t`days`}</InputAdornment>,
                                        },
                                    }}
                                />
                            </StyledTableCell>
                        </TableRow>

                        <TableRow>
                            <StyledTableCell>
                                <Tag label={transEnum(SolutionTag.Expired, solutionTagTranslations)} color="red" />
                            </StyledTableCell>
                            <StyledTableCell>
                                <Flexbox gap={'4px'} alignItems="center">
                                    <Text>{t`Valid until date was `}</Text>
                                    <Text variant="body-semibold">
                                        {formatDecimal(expiredAfterValidityDate, { ifAbsent: '∞' })}
                                    </Text>
                                    <Text>{t` days ago`}</Text>
                                </Flexbox>
                            </StyledTableCell>
                            <StyledTableCell>
                                <FieldNumericControlled
                                    name="solutionTagsPreferences.offerAgeLimit.expiredAfterValidityDate"
                                    control={control}
                                    min={0}
                                    isInteger
                                    FieldProps={{
                                        placeholder: t`Leave empty to disable`,
                                        size: 'small',
                                        InputProps: {
                                            endAdornment: <InputAdornment position="end">{t`days`}</InputAdornment>,
                                        },
                                    }}
                                />
                            </StyledTableCell>
                        </TableRow>
                    </TableBody>
                </StyledTable>
            </Box>
            <Text variant="body-small" color={colorSystem.neutral[7]}>
                {t`Outdated and expired offers get sorted to the bottom, regardless of other solution preferences.`}
            </Text>
        </FormItem>
    );
};

export function SolutionTagPreferncesTab() {
    return (
        <Flexbox
            flexDirection="column"
            style={{ width: '100%', alignSelf: 'center', alignItems: 'center', overflow: 'auto' }}
        >
            <Flexbox flexDirection="column" gap={'28px'} width={'800px'} alignSelf="center">
                <OfferAgeLimitSection />
            </Flexbox>
        </Flexbox>
    );
}
